import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, ArrowRight, Pencil, Check, Star } from "lucide-react";
import { Icon } from "@iconify/react";
import { api } from "../../lib/api";
import { useBooking } from "../../lib/BookingContext";
import { resolveCategoryIcon } from "../../lib/categoryIcons";

function LocationPicker({ locations, value, onChange, disabled, placeholder }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, []);

  const selected = locations.find((l) => l.id === value);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`ws-input w-full text-left flex items-center justify-between gap-2 pr-10 disabled:opacity-60 disabled:cursor-not-allowed ${
          open ? "border-[#0466ff]" : ""
        }`}
        data-testid="location-select"
      >
        {selected ? (
          <span className="flex-1 min-w-0 leading-tight">
            <span className="block truncate text-[#1A1A1A]">{selected.name}</span>
            {selected.subtext && (
              <span className="block truncate text-[11px] text-[#5C5C5C]" data-testid="location-button-subtext">
                {selected.subtext}
              </span>
            )}
          </span>
        ) : (
          <span className="text-[#5C5C5C]">{placeholder}</span>
        )}
        <ChevronDown className={`w-4 h-4 text-[#5C5C5C] flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
      </button>

      {open && !disabled && (
        <div
          className="absolute left-0 right-0 mt-1 z-30 bg-white border border-[#E5E5E5] rounded-md shadow-lg max-h-80 overflow-y-auto"
          data-testid="location-options-panel"
        >
          {locations.map((l) => {
            const isSelected = l.id === value;
            return (
              <button
                key={l.id}
                type="button"
                onClick={() => { onChange(l); setOpen(false); }}
                className={`w-full text-left px-3 py-2.5 flex items-start gap-2 hover:bg-[#F0F6FF] ${
                  isSelected ? "bg-[#F0F6FF]" : ""
                }`}
                data-testid={`location-option-${l.id}`}
              >
                <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isSelected ? "text-[#0466ff]" : "text-transparent"}`} strokeWidth={3} />
                <span className="flex-1 min-w-0 leading-tight">
                  <span className="block text-sm sm:text-[15px] text-[#1A1A1A]">{l.name}</span>
                  {l.subtext && (
                    <span className="block text-[11px] text-[#5C5C5C] mt-0.5">{l.subtext}</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Scroll-aware swipe row with page dots + auto-fading edge gradient.
function SwipeCardRow({ items, renderCard, hintColor = "#0466ff", testId }) {
  const scrollRef = useRef(null);
  const [active, setActive] = useState(0);
  const [atEnd, setAtEnd] = useState(false);

  // Update the active dot + end-of-scroll flag on every scroll.
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const cards = el.querySelectorAll("[data-card]");
    if (!cards.length) return;
    const stride = cards[0].offsetWidth + 12; // gap-3 = 12px
    const idx = Math.round(el.scrollLeft / stride);
    setActive(Math.min(items.length - 1, Math.max(0, idx)));
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  };

  // Recompute on mount/items change.
  useEffect(() => {
    onScroll();
     
  }, [items]);

  const goTo = (i) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelectorAll("[data-card]")[i];
    if (card) el.scrollTo({ left: card.offsetLeft - el.offsetLeft, behavior: "smooth" });
  };

  const showDots = items.length > 1;
  const showFade = items.length > 2 && !atEnd;

  return (
    <div data-testid={testId}>
      <div className="relative">
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-1 -mx-1 px-1 ws-hide-scrollbar"
        >
          {items.map((it, i) =>
            React.cloneElement(renderCard(it), { "data-card": i, key: it.id || i })
          )}
        </div>
        {showFade && (
          <div
            className="md:hidden pointer-events-none absolute top-0 right-0 h-full w-10 bg-gradient-to-l from-[#FAFBFC] via-[#FAFBFC]/80 to-transparent transition-opacity"
            aria-hidden="true"
          />
        )}
      </div>
      {showDots && (
        <div className="md:hidden mt-3 flex items-center justify-center gap-1.5" data-testid={`${testId}-dots`}>
          {items.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => goTo(i)}
              aria-label={`Go to treatment ${i + 1}`}
              className={`transition-all ${i === active ? "w-5 h-1.5 rounded-full bg-[#0466ff]" : "w-1.5 h-1.5 rounded-full bg-[#C9C9C9]"}`}
              data-testid={`${testId}-dot-${i}`}
            />
          ))}
        </div>
      )}
      {items.length > 2 && (
        <p
          className="md:hidden mt-2 text-xs font-bold flex items-center justify-center gap-1.5"
          style={{ color: hintColor }}
        >
          <span className="tracking-wide">SWIPE TO VIEW ALL {items.length} TREATMENTS</span>
          <ArrowRight className="w-3.5 h-3.5 animate-bounce-x" strokeWidth={3} />
        </p>
      )}
    </div>
  );
}

export default function StepServiceLocation() {
  const { service, location, update, next } = useBooking();
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [openCat, setOpenCat] = useState(null); // single-open accordion (null = all closed)
  const [selectedCatId, setSelectedCatId] = useState(null); // which section the patient picked the service from
  const [pickerExpanded, setPickerExpanded] = useState(!service); // collapse the picker once a service is chosen
  const [locationPickerExpanded, setLocationPickerExpanded] = useState(true);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingServices, setLoadingServices] = useState(true);

  // Load services + categories once
  useEffect(() => {
    Promise.all([
      api.services().then(setServices).catch(() => setServices([])),
      api.categories().then(setCategories).catch(() => setCategories([])),
    ]).finally(() => setLoadingServices(false));
  }, []);

  // All categories start closed; openCat is only set when the patient clicks one.
  const displayOpenCat = openCat;

  // Build grouped accordion sections. A service tagged with N categories
  // appears in each. Anything untagged goes in a trailing "Other" section.
  const groups = (() => {
    const byCat = new Map(categories.map((c) => [c.id, []]));
    const uncategorised = [];
    for (const s of services) {
      const ids = s.category_ids && s.category_ids.length ? s.category_ids : (s.category_id ? [s.category_id] : []);
      if (ids.length === 0) {
        uncategorised.push(s);
        continue;
      }
      for (const id of ids) {
        if (byCat.has(id)) byCat.get(id).push(s);
      }
    }
    const out = categories
      .map((c) => ({ id: c.id, label: c.label, icon: c.icon, items: byCat.get(c.id) || [] }))
      .filter((g) => g.items.length > 0);
    if (uncategorised.length) out.push({ id: "__other__", label: "Other", icon: "ellipsis", items: uncategorised });
    return out;
  })();

  // When service changes, refresh locations filtered to ones offering this service
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingLocations(true);
      const data = await api.locations(service?.id);
      if (cancelled) return;
      setLocations(data);
      // If the previously-selected location no longer offers this service, clear it
      if (service && location && !data.find((l) => l.id === location.id)) {
        update({ location: null, slot: null });
      }
      setLoadingLocations(false);
    })();
    return () => { cancelled = true; };
     
  }, [service?.id]);

  const canContinue = !!service && !!location;

  const renderServiceRow = (s, fromCatId, hasSubtextInGroup) => {
    const sel = service?.id === s.id;
    const isOffer = /^special offer/i.test(s.name || "");
    return (
      <button
        key={s.id}
        type="button"
        onClick={() => {
          update({ service: s, slot: null });
          setSelectedCatId(fromCatId);
          setPickerExpanded(false);
          setLocationPickerExpanded(true);
        }}
        className={`w-full px-4 py-3 flex items-center gap-3 text-left transition-colors ${
          hasSubtextInGroup ? "min-h-[88px]" : "min-h-[68px]"
        } ${sel ? "bg-[#EAEAEA]" : "hover:bg-[#F2F2F2]"}`}
        data-testid={`service-option-${s.id}`}
      >
        <span className={`w-5 h-5 rounded-full flex-shrink-0 grid place-items-center transition-all ${
          sel
            ? "bg-[#1FAD3A] border-2 border-[#1FAD3A]"
            : "border-2 border-[#C9C9C9] bg-white"
        }`}>
          {sel && <Check className="w-3 h-3 text-white" strokeWidth={3.5} />}
        </span>
        <span className="flex-1 min-w-0 flex flex-col gap-1">
          {isOffer && (
            <span
              className="self-start inline-flex items-center gap-0.5 text-[9px] font-bold text-white bg-[#D0021B] px-1.5 py-0.5 rounded-full tracking-wide uppercase"
              data-testid={`service-special-badge-${s.id}`}
            >
              <Star className="w-2.5 h-2.5 text-white fill-white" strokeWidth={2} />
              Offer
            </span>
          )}
          <span className="text-sm leading-snug text-[#1A1A1A]">{s.name}</span>
          {s.subtext && (
            <span className="text-[11px] text-[#5C5C5C] leading-tight" data-testid={`service-subtext-${s.id}`}>{s.subtext}</span>
          )}
        </span>
        <span className="flex-shrink-0 self-center">
          {s.price > 0 ? (
            <span className="text-sm font-semibold text-[#0466ff]">
              €{Number.isInteger(s.price) ? s.price : s.price.toFixed(2)}
            </span>
          ) : (
            <span
              className="inline-block text-[10px] font-bold text-white bg-[#1FAD3A] px-2 py-0.5 rounded-full tracking-wide"
              data-testid={`service-free-pill-${s.id}`}
            >
              FREE
            </span>
          )}
        </span>
      </button>
    );
  };

  return (
    <div data-testid="step-service">
      <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight mb-6" style={{ fontFamily: "Manrope" }}>
        Please select service &amp; location
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:gap-6 max-w-xl">
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <label className="block text-sm font-semibold text-[#0466ff]" style={{ fontFamily: "Manrope" }}>
              Select Service
            </label>
            {service && !pickerExpanded && (
              <button
                type="button"
                onClick={() => {
                  setPickerExpanded(true);
                  if (selectedCatId) setOpenCat(selectedCatId);
                }}
                className="inline-flex items-center gap-1 text-xs font-semibold text-[#0466ff] hover:underline"
                data-testid="change-service-button"
              >
                <Pencil className="w-3 h-3" strokeWidth={2.5} />
                Change service
              </button>
            )}
          </div>

          {service && !pickerExpanded ? (
            <button
              type="button"
              onClick={() => {
                setPickerExpanded(true);
                if (selectedCatId) setOpenCat(selectedCatId);
              }}
              className="w-full rounded-lg bg-white border-2 border-[#0466ff] px-4 py-3 flex items-start gap-2 text-left hover:bg-[#F0F6FF] transition-colors"
              data-testid="selected-service-card"
            >
              <span className="w-5 h-5 mt-0.5 rounded-full bg-[#1FAD3A] flex-shrink-0 grid place-items-center">
                <Check className="w-3 h-3 text-white" strokeWidth={3.5} />
              </span>
              <span className="flex-1 min-w-0 leading-tight">
                <span className="block text-sm text-[#1A1A1A]">{service.name}</span>
                {service.subtext && (
                  <span className="block text-[11px] text-[#5C5C5C] mt-0.5">{service.subtext}</span>
                )}
                {service.price > 0 ? (
                  <span className="block text-[11px] font-semibold text-[#0466ff] mt-0.5">
                    €{Number.isInteger(service.price) ? service.price : service.price.toFixed(2)}
                  </span>
                ) : (
                  <span className="inline-block mt-1 text-[10px] font-bold text-white bg-[#1FAD3A] px-2 py-0.5 rounded-full tracking-wide">
                    FREE
                  </span>
                )}
              </span>
            </button>
          ) : (
            <div className="space-y-2" data-testid="service-accordion">
              {loadingServices ? (
                <>
                  <div className="h-12 rounded-lg bg-white border-2 border-[#E5E5E5] animate-pulse" />
                  <div className="h-12 rounded-lg bg-white border-2 border-[#E5E5E5] animate-pulse" />
                  <div className="h-12 rounded-lg bg-white border-2 border-[#E5E5E5] animate-pulse" />
                </>
              ) : groups.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-[#5C5C5C] border-2 border-[#C9C9C9] rounded-lg bg-white">No treatments configured yet.</div>
              ) : categories.length === 0 ? (
                // Flat list mode: no categories are configured at all, so
                // skip the accordion wrapper entirely and show the treatments
                // directly. Used by the White Smile Clinic site (whitening only)
                // and by any site that hasn't set up categories yet.
                <div className="rounded-lg bg-white border-2 border-[#C9C9C9] overflow-hidden">
                  <ul className="flex flex-col divide-y divide-[#F0F0F0]" data-testid="service-flat-list">
                    {(() => {
                      const flat = services;
                      const hasSubtext = flat.some((s) => s.subtext);
                      return flat.map((s) => (
                        <li key={s.id} className="flex">
                          {renderServiceRow(s, null, hasSubtext)}
                        </li>
                      ));
                    })()}
                  </ul>
                </div>
              ) : groups.map((g) => {
                const isOpen = displayOpenCat === g.id;
                return (
                  <div
                    key={g.id}
                    className={`rounded-lg bg-white border-2 overflow-hidden transition-colors ${
                      isOpen ? "border-[#8C8C8C]" : "border-[#C9C9C9]"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenCat(isOpen ? "__none__" : g.id)}
                      aria-expanded={isOpen}
                      className={`w-full px-4 py-3 flex items-center justify-between text-left transition-colors ${
                        isOpen ? "bg-[#F2F2F2]" : "hover:bg-[#F8F9FB]"
                      }`}
                      data-testid={`cat-acc-header-${g.id}`}
                    >
                      <span className="flex items-center gap-2.5">
                        <Icon
                          icon={resolveCategoryIcon(g.icon)}
                          className="w-5 h-5 text-[#0466ff]"
                          aria-hidden="true"
                        />
                        <span className={`text-sm font-bold text-[#1A1A1A]`} style={{ fontFamily: "Manrope" }}>
                          {g.label}
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-wide bg-[#E8F0FE] text-[#0466ff] px-1.5 py-0.5 rounded">
                          {g.items.length}
                        </span>
                      </span>
                      <ChevronDown className={`w-5 h-5 text-[#1A1A1A] transition-transform ${isOpen ? "rotate-180" : ""}`} strokeWidth={3} />
                    </button>
                    {isOpen && (
                      <div className="border-t border-[#E5E5E5] bg-white" data-testid={`cat-acc-body-${g.id}`}>
                        <ul className="flex flex-col divide-y divide-[#F0F0F0]">
                          {(() => {
                            const groupHasSubtext = g.items.some((s) => s.subtext);
                            return g.items.map((s) => (
                              <li key={s.id} className="flex">
                                {renderServiceRow(s, g.id, groupHasSubtext)}
                              </li>
                            ));
                          })()}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-baseline justify-between mb-2">
            <label className="block text-sm font-semibold text-[#0466ff]" style={{ fontFamily: "Manrope" }}>
              Select Location
            </label>
            {location && !locationPickerExpanded && (
              <button
                type="button"
                onClick={() => setLocationPickerExpanded(true)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-[#0466ff] hover:underline"
                data-testid="change-location-button"
              >
                <Pencil className="w-3 h-3" strokeWidth={2.5} />
                Change location
              </button>
            )}
          </div>

          {!service ? (
            <div
              className="rounded-lg bg-white border-2 border-[#C9C9C9] px-4 py-3 text-sm text-[#5C5C5C]"
              data-testid="location-placeholder"
            >
              Pick a service first
            </div>
          ) : loadingLocations ? (
            <div className="rounded-lg bg-white border-2 border-[#C9C9C9] px-4 py-3 text-sm text-[#5C5C5C]">Loading clinics…</div>
          ) : locations.length === 0 ? (
            <div className="rounded-lg bg-white border-2 border-[#C9C9C9] px-4 py-3 text-sm text-[#C85A41]" data-testid="no-locations-msg">
              No clinic is currently set up for this service. Please call us on 01 667 8524.
            </div>
          ) : location && !locationPickerExpanded ? (
            <button
              type="button"
              onClick={() => setLocationPickerExpanded(true)}
              className="w-full rounded-lg bg-white border-2 border-[#0466ff] px-4 py-3 flex items-start gap-2 text-left hover:bg-[#F0F6FF] transition-colors"
              data-testid="selected-location-card"
            >
              <span className="w-5 h-5 mt-0.5 rounded-full bg-[#1FAD3A] flex-shrink-0 grid place-items-center">
                <Check className="w-3 h-3 text-white" strokeWidth={3.5} />
              </span>
              <span className="flex-1 min-w-0 leading-tight">
                <span className="block text-sm text-[#1A1A1A]">{location.name}</span>
                {location.subtext && (
                  <span className="block text-[11px] text-[#5C5C5C] mt-0.5">{location.subtext}</span>
                )}
              </span>
            </button>
          ) : (
            <div className="space-y-2" data-testid="location-cards">
              {locations.map((l) => {
                const sel = location?.id === l.id;
                return (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => {
                      update({ location: l, slot: null });
                      setLocationPickerExpanded(false);
                    }}
                    className={`w-full rounded-lg bg-white border-2 px-4 py-3 flex items-start gap-2 text-left transition-colors ${
                      sel ? "border-[#0466ff] bg-[#F0F6FF]" : "border-[#C9C9C9] hover:bg-[#F8F9FB]"
                    }`}
                    data-testid={`location-option-${l.id}`}
                  >
                    <span className={`w-5 h-5 mt-0.5 rounded-full flex-shrink-0 grid place-items-center transition-all ${
                      sel
                        ? "bg-[#1FAD3A] border-2 border-[#1FAD3A]"
                        : "border-2 border-[#C9C9C9] bg-white"
                    }`}>
                      {sel && <Check className="w-3 h-3 text-white" strokeWidth={3.5} />}
                    </span>
                    <span className="flex-1 min-w-0 leading-tight">
                      <span className="block text-sm text-[#1A1A1A]">{l.name}</span>
                      {l.subtext && (
                        <span className="block text-[11px] text-[#5C5C5C] mt-0.5">{l.subtext}</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="mt-8 sm:mt-10 flex justify-end">
        <button
          className="ws-btn-primary inline-flex items-center gap-2"
          disabled={!canContinue}
          onClick={next}
          data-testid="service-next-button"
        >
          NEXT <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}
